import { useModal } from '../../providers/ModalProvider/ModalProvider'
import { PrintTicketsProvider } from '../../providers/PrintTicketsProvider/PrintTicketsProvider'

import { constants } from '@blocktickets/shared-utils'

import { ConfirmSendTickets } from '../../components/ModalContent/ConfirmSendTickets'
import { ConfirmReleaseRefundOrder } from '../../components/ModalContent/ConfirmReleaseRefundOrder'
import { ViewAssociatedTo } from '../../components/ModalContent/ViewAssociatedTo'
import { ViewInvoices } from '../../components/ModalContent/ViewInvoices'
import { ViewProfile } from '../../components/ModalContent/ViewProfile'
import { InvoiceHtml } from '../../components/ModalContent/InvoiceHtml'
import PrintTicketsForOrder from '../../components/ModalContent/PrintTicketsForOrder/PrintTicketsForOrder'
import { PrintVouchersForOrder } from '../../components/ModalContent/PrintVouchersForOrder'
import { ViewVouchers } from './../../components/ModalContent/ViewVouchers'
import { MarkAsPaid } from '../../components/ModalContent/MarkAsPaid'
import { MarkAsComplete } from '../../components/ModalContent'
import RelocateSeasonSeats from '../../components/ModalContent/RelocateSeasonSeats/RelocateSeasonSeats'
import AddSeasonSeats from '../../components/ModalContent/AddSeasonSeats/AddSeasonSeats'
import RemoveSeasonSeats from '../../components/ModalContent/RemoveSeasonSeats/RemoveSeasonSeats'
import RemoveEventTickets from '../../components/ModalContent/RemoveEventTickets/RemoveEventTickets'
import { UndoReleaseOrder } from '../../components/ModalContent/UndoReleaseOrder'
import AssignTicketsModalContentV2 from '../../providers/AssignToProvider/AssignTicketsModalContent/AssignTicketsModalContentV2'

export const useOrderActions = (runSearch) => {
  const { openModal } = useModal()

  const { ORDER_STATUS } = constants

  const openViewOrder = (order) => {
    openModal(
      () => (
        <PrintTicketsProvider>
          <PrintTicketsForOrder order={order} />
        </PrintTicketsProvider>
      ),
      {},
      { className: 'vjx-modal-2xl', contentClassName: '', scrollable: true }
    )
  }

  const openViewProfile = (order) => {
    openModal(
      () => (
        <PrintTicketsProvider>
          <ViewProfile order={order} runSearch={runSearch} />
        </PrintTicketsProvider>
      ),
      {},
      {
        className: 'vjx-modal-2xl',
        contentClassName: 'h-lg-fixed',
        scrollable: true,
      }
    )
  }

  const openViewInvoices = (order) => {
    openModal(
      ViewInvoices,
      { order },
      { className: 'modal-lg', contentClassName: '', scrollable: true }
    )
  }

  const openInvoiceHtml = (order) => {
    openModal(
      InvoiceHtml,
      { order },
      { className: 'vjx-modal-2xl', contentClassName: '', scrollable: true }
    )
  }

  const openViewAssociatedTo = (order) => {
    openModal(
      ViewAssociatedTo,
      { order, onSuccess: runSearch },
      { className: 'modal-lg', contentClassName: '' }
    )
  }

  const openConfirmRefund = (order) => {
    openModal(
      ConfirmReleaseRefundOrder,
      {
        order,
        onSuccess: runSearch,
        isRefunding: true,
        isRenewing: order?.status === ORDER_STATUS.RESERVED,
      },
      { className: 'modal--lg', contentClassName: '' }
    )
  }

  const openReleaseTickets = (order) => {
    openModal(
      ConfirmReleaseRefundOrder,
      {
        order,
        onSuccess: runSearch,
      },
      { className: 'modal--lg', contentClassName: '' }
    )
  }

  const openCancelOrder = (order) => {
    openModal(
      ConfirmReleaseRefundOrder,
      {
        order,
        onSuccess: runSearch,
        isCancelling: true,
        isRenewing: false,
      },
      { className: 'modal--lg', contentClassName: '' }
    )
  }

  const openVoidOrder = (order) => {
    openModal(
      ConfirmReleaseRefundOrder,
      {
        order,
        onSuccess: runSearch,
        isVoiding: true,
      },
      { className: 'modal--lg', contentClassName: '' }
    )
  }

  const openConfirmSendTickets = (order, onSuccess) => {
    openModal(
      ConfirmSendTickets,
      {
        order,
        onSuccess: onSuccess, // sometimes just toggle, sometimes reload
      },
      { className: 'modal--lg', contentClassName: '' }
    )
  }

  const openMarkAsPaid = (order, runSearch, options = {}) => {
    openModal(
      MarkAsPaid,
      {
        order,
        reloadAssignments: runSearch,
        cashOrCheckOnly: Boolean(options.cashOrCheckOnly),
      },
      { className: 'modal-lg', contentClassName: '' }
    )
  }

  const openViewVouchers = (vouchers, onClose, replace = true) => {
    openModal(
      ViewVouchers,
      { vouchers, onClose },
      { className: '', contentClassName: '', scrollable: true },
      { replace: replace }
    )
  }

  const openPrintVouchers = (order) => {
    openModal(
      () => (
        <PrintTicketsProvider>
          <PrintVouchersForOrder order={order} />
        </PrintTicketsProvider>
      ),
      {},
      { className: 'vjx-modal-2xl', contentClassName: '', scrollable: true }
    )
  }

  const openMarkAsComplete = (order) => {
    openModal(
      MarkAsComplete,
      { order },
      { className: 'modal-lg', contentClassName: '' }
    )
  }

  const openRelocateSeats = (order) => {
    openModal(
      RelocateSeasonSeats,
      { order, onSuccess: runSearch },
      {
        className: 'modal-xl modal-full',
        contentClassName: 'h-lg',
        scrollable: true,
      }
    )
  }

  const openAddSeats = (order) => {
    openModal(
      AddSeasonSeats,
      { order, onSuccess: runSearch },
      {
        className: 'modal-xl modal-full',
        contentClassName: 'h-lg',
        scrollable: true,
      }
    )
  }

  const openAddCustomFee = (order) => {
    const customer = order?.users_permissions_user || {}
    const initialAssignTo = {
      firstName: order?.firstName || customer?.firstName || '',
      lastName: order?.lastName || customer?.lastName || '',
      email: order?.email || customer?.email || '',
      phoneNumber: order?.phoneNumber || customer?.phoneNumber || null,
      organizationName: order?.purchasedByOrganizationName || '',
    }
    const initialExistingUser = initialAssignTo.email
      ? {
          label:
            `${initialAssignTo.firstName} ${initialAssignTo.lastName}`.trim(),
          ...initialAssignTo,
          custom: initialAssignTo.email,
        }
      : undefined
    const eventPackage = {
      ...(order?.package || {}),
      uuid:
        order?.package?.uuid ||
        order?.packageUuid ||
        order?.package_uuid ||
        order?.packageUUID,
      organizationUUID:
        order?.package?.organizationUUID || order?.organizationUUID,
    }
    const buyerTypeId = Number(
      order?.buyer_type?.id ?? order?.buyerType?.id ?? order?.buyer_type
    )

    openModal(
      AssignTicketsModalContentV2,
      {
        eventPackage,
        selectedSeatIds: {},
        data: { seats: {}, sections: {} },
        initialAssignTo,
        initialExistingUser,
        addCustomFeeToOrderId: order?.id,
        sourceOrderBuyerTypeId:
          Number.isFinite(buyerTypeId) && buyerTypeId > 0
            ? buyerTypeId
            : undefined,
        reloadAssignments: runSearch,
      },
      { className: 'modal-xl', contentClassName: 'h-md' }
    )
  }

  const openRemoveSeats = (order) => {
    openModal(
      RemoveSeasonSeats,
      { order, onSuccess: runSearch },
      {
        className: 'modal-xl modal-full',
        contentClassName: 'h-lg',
        scrollable: true,
      }
    )
  }

  const openRemoveEventTickets = (order) => {
    openModal(
      RemoveEventTickets,
      { order, onSuccess: runSearch },
      {
        className: 'modal-xl modal-full',
        contentClassName: 'h-lg',
        scrollable: true,
      }
    )
  }

  const openUndoRelease = (order) => {
    openModal(
      UndoReleaseOrder,
      { order, onSuccess: runSearch },
      { className: 'modal--lg', contentClassName: '' }
    )
  }

  return {
    openViewOrder,
    openViewAssociatedTo,
    openViewInvoices,
    openInvoiceHtml,
    openViewProfile,
    openConfirmRefund,
    openReleaseTickets,
    openCancelOrder,
    openVoidOrder,
    openConfirmSendTickets,
    openMarkAsPaid,
    openViewVouchers,
    openPrintVouchers,
    openMarkAsComplete,
    openRelocateSeats,
    openAddSeats,
    openAddCustomFee,
    openRemoveSeats,
    openRemoveEventTickets,
    openUndoRelease,
  }
}
